import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Form,
    Input,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    FilePdfFilled,
    PlusOutlined,
    RollbackOutlined,
} from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { modalError } from '../../Component/Modal/ModalLayout';
import { useSelector } from 'react-redux';
import Search from 'antd/es/input/Search';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

function AssetRequest() {
    const navigate = useNavigate();
    const token = useSelector((state) => state.user.token);
    const user = useSelector((state) => state.user);

    const [data, setData] = useState([]);
    const [assetOptions, setAssetOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterApprovalStatus, setFilterApprovalStatus] = useState('');
    const [filterDelegationStatus, setFilterDelegationStatus] = useState('');

    const [isModal, setIsModal] = useState(false);
    const [dataModal, setDataModal] = useState(null);

    const [isDetailModal, setIsDetailModal] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [analyticAssignment, setAnalyticAssignment] = useState({
        total_assignments: 0,
        pending_approval: 0,
        approved_assignments: 0,
        rejected_assignments: 0,
        returned_assignments: 0,
        assigned_assets: 0,
    });
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);

    const [form] = Form.useForm();

    const [page, setPage] = useState({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
    });

    const [isAlert, setIsAlert] = useState(false);
    const [textAlert, setTextAlert] = useState('');

    const titlePage = 'Asset Request Data';

    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'assigned', label: 'Assigned' },
        { value: 'returned', label: 'Returned' },
        { value: 'rejected', label: 'Rejected' },
    ];

    const approvalStatusOptions = [
        { value: 'Waiting Authorization', label: 'Waiting Authorization' },
        { value: 'Pending', label: 'Pending Final' },
        { value: 'Approved', label: 'Approved' },
        { value: 'Rejected', label: 'Rejected' },
    ];

    const delegationStatusOptions = [
        { value: 'Pending', label: 'Pending' },
        { value: 'Approved', label: 'Approved' },
        { value: 'Rejected', label: 'Rejected' },
    ];
    const assignDateValue = Form.useWatch('assign_date', form);

    const getHeaders = () => ({
        Authorization: 'Bearer ' + token,
    });

    const getLoginUserId = () => {
        try {
            return JSON.parse(user.user)?.users_id;
        } catch {
            return null;
        }
    };

    const getApprovalStatus = (record) => {
        return record?.approval_status || record?.approval?.status || '-';
    };

    const getDelegationStatus = (record) => {
        return record?.delegation_status || record?.approval?.delegation_status || '-';
    };

    const getApprovalUser = (record) => {
        return record?.approval?.approval_user?.name || '-';
    };

    const getDelegationUser = (record) => {
        return record?.approval?.delegation_user?.name || '-';
    };

    const renderStatus = (status) => {
        if (status === 'pending') return <Tag color="orange">Pending</Tag>;
        if (status === 'assigned') return <Tag color="blue">Assigned</Tag>;
        if (status === 'returned') return <Tag color="green">Returned</Tag>;
        if (status === 'rejected') return <Tag color="red">Rejected</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const renderApprovalStatus = (status) => {
        if (status === 'Waiting Authorization') return <Tag color="purple">Waiting Authorization</Tag>;
        if (status === 'Pending') return <Tag color="orange">Pending Final</Tag>;
        if (status === 'Approved') return <Tag color="green">Approved</Tag>;
        if (status === 'Rejected') return <Tag color="red">Rejected</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const renderDelegationStatus = (status) => {
        if (status === 'Pending') return <Tag color="orange">Pending</Tag>;
        if (status === 'Approved') return <Tag color="green">Approved</Tag>;
        if (status === 'Rejected') return <Tag color="red">Rejected</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const requestPercent = useMemo(() => {
        const total = Number(analyticAssignment?.total_assignments || 0);

        if (!total) {
            return {
                pending: 0,
                approved: 0,
                rejected: 0,
                returned: 0,
                assigned: 0,
            };
        }

        return {
            pending: Math.round((Number(analyticAssignment?.pending_approval || 0) / total) * 100),
            approved: Math.round((Number(analyticAssignment?.approved_assignments || 0) / total) * 100),
            rejected: Math.round((Number(analyticAssignment?.rejected_assignments || 0) / total) * 100),
            returned: Math.round((Number(analyticAssignment?.returned_assignments || 0) / total) * 100),
            assigned: Math.round((Number(analyticAssignment?.assigned_assets || 0) / total) * 100),
        };
    }, [analyticAssignment]);

    const columns = [
        {
            title: 'No',
            key: 'no',
            width: 70,
            render: (_, __, index) =>
                (page.pagination.current - 1) * page.pagination.pageSize + index + 1,
        },
        {
            title: 'Asset Request Code',
            key: 'asset_code',
            width: 130,
            render: (_, record) => record?.asset?.asset_code || '-',
        },
        {
            title: 'Category',
            key: 'category',
            width: 150,
            render: (_, record) => record?.category?.name || '-',
        },
        {
            title: 'Asset',
            key: 'asset',
            width: 200,
            render: (_, record) => {
                const asset = record?.asset;

                if (!asset) return '-';

                return [
                    asset.asset_name,
                    asset.model,
                    asset.serial_number ? `(${asset.serial_number})` : '',
                ]
                    .filter(Boolean)
                    .join(' ');
            },
        },
        {
            title: 'Request Date',
            dataIndex: 'request_date',
            key: 'request_date',
            width: 130,
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : '-'),
        },
        {
            title: 'Assign Date',
            dataIndex: 'assign_date',
            key: 'assign_date',
            width: 130,
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : '-'),
        },
        {
            title: 'Return Date',
            dataIndex: 'return_date',
            key: 'return_date',
            width: 130,
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : '-'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (text) => renderStatus(text),
        },
        {
            title: 'Authorization',
            key: 'delegation_status',
            width: 130,
            render: (_, record) => renderDelegationStatus(getDelegationStatus(record)),
        },
        {
            title: 'Approval',
            key: 'approval_status',
            width: 140,
            render: (_, record) => renderApprovalStatus(getApprovalStatus(record)),
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            width: 220,
            render: (text) => text || '-',
        },
        {
            title: 'Action',
            key: 'action',
            width: 190,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Detail">
                        <Button onClick={() => openDetailModal(record)}>
                            <EyeOutlined />
                        </Button>
                    </Tooltip>

                    <Tooltip title="Edit">
                        <Button
                            type="primary"
                            onClick={() => openModal('edit', record)}
                            disabled={getApprovalStatus(record) === 'Approved' || record.status !== 'pending'}
                        >
                            <EditOutlined />
                        </Button>
                    </Tooltip>

                    <Tooltip title="Delete">
                        <Button
                            danger
                            onClick={() => modalDeleteData(record)}
                            disabled={getApprovalStatus(record) === 'Approved' || record.status !== 'pending'}
                        >
                            <DeleteOutlined />
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        loadAssetRequests();
    }, [
        search,
        filterStatus,
        filterApprovalStatus,
        filterDelegationStatus,
        page.pagination.current,
        page.pagination.pageSize,
    ]);

    useEffect(() => {
        loadAssetCategories();
        loadAnalytics();
    }, []);

    const loadAssetRequests = () => {
        const params = new URLSearchParams({
            search: search || '',
            page: page.pagination.current,
            limit: page.pagination.pageSize,
        });

        if (filterStatus) params.append('status', filterStatus);
        if (filterApprovalStatus) params.append('approval_status', filterApprovalStatus);
        if (filterDelegationStatus) params.append('delegation_status', filterDelegationStatus);

        const usersId = getLoginUserId();
        if (usersId) params.append('users_id', usersId);

        getApi('GET', `/asset-assignments?${params.toString()}`);
    };

    const loadAnalytics = async () => {
        setLoadingAnalytics(true);

        try {
            const assignmentRes = await apiCall({
                method: 'GET',
                url: '/asset-analytics/assignment',
                data: { headers: getHeaders() },
            });

            if (assignmentRes?.status === 'success') {
                setAnalyticAssignment(assignmentRes?.data || {});
            }
        } catch (e) {
            console.log(e.message);
        } finally {
            setLoadingAnalytics(false);
        }
    };

    const loadAssetCategories = () => {
        apiCall({
            method: 'GET',
            url: '/asset-categories?nopage=1',
            data: {
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    const list = Array.isArray(res?.data?.data) ? res.data.data : [];
                    const options = list.map((item) => ({
                        value: item.asset_category_id,
                        label: item.name,
                    }));
                    setAssetOptions(options);
                }
            })
            .catch((e) => {
                console.log(e.message);
            });
    };

    const getApi = (method, url) => {
        setLoading(true);

        apiCall({
            method,
            url,
            data: {
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    setData(res.data.data || []);
                    setPage((prev) => ({
                        ...prev,
                        pagination: {
                            ...prev.pagination,
                            total: res.data.total_data || 0,
                        },
                    }));
                } else {
                    modalError(res.message || 'Gagal mengambil data');
                }

                setLoading(false);
            })
            .catch((e) => {
                setLoading(false);
                modalError(e.message);
            });
    };

    const getDetailApi = (id) => {
        setLoadingDetail(true);

        apiCall({
            method: 'GET',
            url: `/asset-assignments/${id}`,
            data: {
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    setDetailData(res.data);
                    setIsDetailModal(true);
                } else {
                    modalError(res.message || 'Gagal mengambil detail');
                }

                setLoadingDetail(false);
            })
            .catch((e) => {
                setLoadingDetail(false);
                modalError(e.message);
            });
    };

    const postApi = async (method, url, values) => {
        setLoading(true);
        setIsAlert(false);

        await apiCall({
            method,
            url,
            data: {
                data: values,
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    setDataModal(null);
                    setIsModal(false);
                    setIsAlert(true);
                    setTextAlert(res.message);
                    form.resetFields();
                    loadAssetRequests();
                    loadAnalytics();
                } else {
                    modalError(res.message);
                }

                setLoading(false);
            })
            .catch((e) => {
                setLoading(false);
                modalError(e.message);
            });
    };

    const modalDeleteData = (record) => {
        Modal.confirm({
            title: 'NOTICE',
            content: 'Do you want to delete this request?',
            onOk: () => postApi('DELETE', '/asset-assignments/' + record.asset_assignment_id, null),
        });
    };

    const openModal = (type, data) => {
        if (type === 'edit' && data) {
            setDataModal(data);
            form.setFieldsValue({
                asset_category_id:
                    data.asset_category_id ||
                    data?.asset_category?.asset_category_id ||
                    data?.category?.asset_category_id ||
                    data?.asset?.asset_category_id ||
                    data?.asset?.category?.asset_category_id,
                request_date: data.request_date ? dayjs(data.request_date) : null,
                assign_date: data.assign_date ? dayjs(data.assign_date) : null,
                return_date: data.return_date ? dayjs(data.return_date) : null,
                notes: data.notes,
            });
        } else {
            setDataModal(null);
            form.resetFields();
            form.setFieldsValue({
                request_date: dayjs(),
            });
        }

        setIsModal(true);
    };

    const openDetailModal = (record) => {
        getDetailApi(record.asset_assignment_id);
    };

    const onFinish = (values) => {
        const payload = {
            ...values,
            request_date: values.request_date ? dayjs(values.request_date).format('YYYY-MM-DD') : '',
            assign_date: values.assign_date ? dayjs(values.assign_date).format('YYYY-MM-DD') : '',
            return_date: values.return_date ? dayjs(values.return_date).format('YYYY-MM-DD') : '',
        };

        if (dataModal) {
            postApi('PUT', '/asset-assignments/' + dataModal.asset_assignment_id, payload);
        } else {
            postApi('POST', '/asset-assignments', payload);
        }
    };

    const closeModal = () => {
        setDataModal(null);
        setIsModal(false);
        form.resetFields();
    };

    const closeDetailModal = () => {
        setIsDetailModal(false);
        setDetailData(null);
    };

    const handleTableChange = (pagination) => {
        setPage({
            pagination,
        });

        if (pagination.pageSize !== page.pagination?.pageSize) {
            setData([]);
        }
    };

    const searchData = (value) => {
        setSearch(value);
        setPage((prev) => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                current: 1,
            },
        }));
    };

    const resetFilter = () => {
        setSearch('');
        setFilterStatus('');
        setFilterApprovalStatus('');
        setFilterDelegationStatus('');
        setPage({
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
            },
        });
    };

    const returnAsset = (record) => {
        Modal.confirm({
            title: 'Pengembalian Asset',
            content: 'Apakah asset ini sudah dikembalikan?',
            okText: 'Ya, Kembalikan',
            cancelText: 'Batal',
            onOk: () =>
                postApi('PUT', `/asset-assignments/${record.asset_assignment_id}/return`, {
                    return_date: dayjs().format('YYYY-MM-DD'),
                    notes: 'Asset dikembalikan',
                }),
        });
    };

    useEffect(() => {
        const returnDate = form.getFieldValue('return_date');

        if (
            assignDateValue &&
            returnDate &&
            dayjs(returnDate).isBefore(dayjs(assignDateValue), 'day')
        ) {
            form.setFieldValue('return_date', null);
        }
    }, [assignDateValue, form]);

    const ApprovalLine = ({ data }) => {
        const approval = data?.approval;

        const steps = [
            {
                title: 'Requester',
                name: data?.user?.name || approval?.user?.name || '-',
                status: 'Submitted',
                date: data?.created_at,
                notes: data?.notes || approval?.notes,
            },
            {
                title: 'Authorization',
                name: approval?.delegation_user?.name || '-',
                status: approval?.delegation_status || '-',
                date: approval?.delegation_date,
                notes: approval?.delegation_notes,
            },
            {
                title: 'Final Approval',
                name: approval?.approval_user?.name || '-',
                status: approval?.status || '-',
                date: approval?.approval_date,
                notes: approval?.approval_notes,
            },
        ];

        const colorByStatus = (status) => {
            if (status === 'Approved' || status === 'Submitted') return '#52c41a';
            if (status === 'Pending' || status === 'Waiting Authorization') return '#faad14';
            if (status === 'Rejected') return '#ff4d4f';
            if (status === 'Skipped') return '#1677ff';
            return '#d9d9d9';
        };

        return (
            <Card title="Approval Line" size="small" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                    {steps.map((item, index) => (
                        <Col span={8} key={item.title}>
                            <div
                                style={{
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 10,
                                    padding: 14,
                                    minHeight: 150,
                                    position: 'relative',
                                    background: '#fff',
                                }}
                            >
                                {index < steps.length - 1 && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 32,
                                            right: -16,
                                            width: 16,
                                            height: 2,
                                            background: '#d9d9d9',
                                        }}
                                    />
                                )}

                                <div
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        background: colorByStatus(item.status),
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        marginBottom: 10,
                                    }}
                                >
                                    {index + 1}
                                </div>

                                <div style={{ fontWeight: 700, marginBottom: 4 }}>{item.title}</div>
                                <div style={{ color: '#555', marginBottom: 8 }}>{item.name}</div>

                                <Tag color={
                                    item.status === 'Approved' || item.status === 'Submitted'
                                        ? 'green'
                                        : item.status === 'Rejected'
                                            ? 'red'
                                            : item.status === 'Skipped'
                                                ? 'blue'
                                                : 'orange'
                                }>
                                    {item.status}
                                </Tag>

                                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                                    {item.date ? dayjs(item.date).format('YYYY-MM-DD HH:mm') : '-'}
                                </div>

                                {item.notes && (
                                    <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>
                                        “{item.notes}”
                                    </div>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>
            </Card>
        );
    };

    return (
        <div>
            <Helmet>
                <title>{titlePage}</title>
            </Helmet>

            {isAlert && (
                <Alert
                    message={textAlert}
                    type="success"
                    banner
                    closable
                    afterClose={() => setIsAlert(false)}
                    style={{ marginBottom: 16 }}
                />
            )}

            <h3>Asset Request</h3>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card loading={loadingAnalytics}>
                        <Statistic title="Total Requests" value={analyticAssignment?.total_assignments || 0} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card loading={loadingAnalytics}>
                        <Statistic title="Pending Approval" value={analyticAssignment?.pending_approval || 0} />
                        <Progress percent={requestPercent.pending} size="small" showInfo={false} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card loading={loadingAnalytics}>
                        <Statistic title="Approved" value={analyticAssignment?.approved_assignments || 0} />
                        <Progress percent={requestPercent.approved} size="small" showInfo={false} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card loading={loadingAnalytics}>
                        <Statistic title="Rejected" value={analyticAssignment?.rejected_assignments || 0} />
                        <Progress percent={requestPercent.rejected} size="small" showInfo={false} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card loading={loadingAnalytics}>
                        <Statistic title="Assigned" value={analyticAssignment?.assigned_assets || 0} />
                        <Progress percent={requestPercent.assigned} size="small" showInfo={false} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card loading={loadingAnalytics}>
                        <Statistic title="Returned" value={analyticAssignment?.returned_assignments || 0} />
                        <Progress percent={requestPercent.returned} size="small" showInfo={false} />
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Search
                        placeholder="Search asset"
                        allowClear
                        enterButton="Search"
                        size="middle"
                        onSearch={searchData}
                    />
                </Col>

                <Col span={4}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filter Status"
                        allowClear
                        value={filterStatus || undefined}
                        options={statusOptions}
                        onChange={(value) => {
                            setFilterStatus(value || '');
                            setPage((prev) => ({
                                ...prev,
                                pagination: {
                                    ...prev.pagination,
                                    current: 1,
                                },
                            }));
                        }}
                    />
                </Col>

                <Col span={4}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filter Authorization"
                        allowClear
                        value={filterDelegationStatus || undefined}
                        options={delegationStatusOptions}
                        onChange={(value) => {
                            setFilterDelegationStatus(value || '');
                            setPage((prev) => ({
                                ...prev,
                                pagination: {
                                    ...prev.pagination,
                                    current: 1,
                                },
                            }));
                        }}
                    />
                </Col>

                <Col span={4}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filter Approval"
                        allowClear
                        value={filterApprovalStatus || undefined}
                        options={approvalStatusOptions}
                        onChange={(value) => {
                            setFilterApprovalStatus(value || '');
                            setPage((prev) => ({
                                ...prev,
                                pagination: {
                                    ...prev.pagination,
                                    current: 1,
                                },
                            }));
                        }}
                    />
                </Col>

                <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={resetFilter}>Refresh</Button>
                    <Button type="primary" onClick={() => openModal('add', null)}>
                        <PlusOutlined />
                        <b style={{ marginLeft: 5 }}>Add</b>
                    </Button>
                </Col>
            </Row>

            <Table
                size="middle"
                columns={columns}
                dataSource={data}
                pagination={page.pagination}
                loading={loading}
                onChange={handleTableChange}
                rowKey={(record) => record.asset_assignment_id}
                scroll={{ x: 2100 }}
            />

            <Modal
                title={dataModal ? 'Edit Asset Request' : 'Add Asset Request'}
                open={isModal}
                onOk={form.submit}
                onCancel={closeModal}
                okText="Save"
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="asset_category_id"
                                label="Asset Category"
                                rules={[{ required: true, message: 'Asset Category is required!' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Select asset category"
                                    options={assetOptions}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="request_date" label="Request Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item name="assign_date" label="Assign Date" rules={[{ required: true, message: 'Assign Date is required!' }]}>
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item
                                name="return_date"
                                label="Return Date"
                                dependencies={['assign_date']}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const assignDate = getFieldValue('assign_date');

                                            if (!value || !assignDate) {
                                                return Promise.resolve();
                                            }

                                            if (dayjs(value).isBefore(dayjs(assignDate), 'day')) {
                                                return Promise.reject(
                                                    new Error('Return Date tidak boleh sebelum Assign Date')
                                                );
                                            }

                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="YYYY-MM-DD"
                                    disabledDate={(current) => {
                                        if (!assignDateValue) return false;

                                        return current && current.isBefore(dayjs(assignDateValue), 'day');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={4} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Detail Asset Request"
                open={isDetailModal}
                onCancel={closeDetailModal}
                footer={[
                    <div
                        key="footer"
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: 12,
                        }}
                    >
                        <Space>
                            <Tooltip title="Preview Surat Tanda Terima">
                                <Button
                                    type="primary"
                                    icon={<FilePdfFilled />}
                                    onClick={() =>
                                        navigate('/assetapproval/receipt', {
                                            state: {
                                                id: detailData?.asset_assignment_id,
                                                type: 'ISSUE',
                                            },
                                        })
                                    }
                                    disabled={detailData?.status !== 'assigned'}
                                >
                                    Surat Terima
                                </Button>
                            </Tooltip>

                            <Tooltip title="Pengembalian Asset">
                                <Button
                                    type="primary"
                                    danger
                                    icon={<RollbackOutlined />}
                                    onClick={() => returnAsset(detailData)}
                                    disabled={
                                        detailData?.status !== 'assigned'
                                    }
                                >
                                    Pengembalian
                                </Button>
                            </Tooltip>

                            <Tooltip title="Preview Laporan Pengembalian Asset">
                                <Button
                                    icon={<FilePdfFilled />}
                                    onClick={() =>
                                        navigate('/assetapproval/receipt', {
                                            state: {
                                                id: detailData?.asset_assignment_id,
                                                type: 'RETURN',
                                            },
                                        })
                                    }
                                    disabled={detailData?.status !== 'returned'}
                                >
                                    Laporan Pengembalian
                                </Button>
                            </Tooltip>
                        </Space>

                        <Button onClick={closeDetailModal}>Close</Button>
                    </div>,
                ]}
                width={900}
            >
                <Descriptions bordered column={2} size="middle" loading={loadingDetail}>
                    <Descriptions.Item label="Asset Code">
                        {detailData?.asset?.asset_code || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Category">
                        {detailData?.category?.name || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Asset">
                        {detailData?.asset
                            ? [
                                detailData?.asset?.asset_name,
                                detailData?.asset?.model,
                                detailData?.asset?.serial_number
                                    ? `(${detailData.asset.serial_number})`
                                    : '',
                            ]
                                .filter(Boolean)
                                .join(' ')
                            : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Asset Status">
                        {detailData?.asset?.status?.name || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Brand">
                        {detailData?.asset?.brand || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Model">
                        {detailData?.asset?.model || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Serial Number">
                        {detailData?.asset?.serial_number || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Condition">
                        {detailData?.asset?.condition_status || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Assigned User">
                        {detailData?.asset?.user?.name || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Asset Location">
                        {detailData?.asset?.location || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Requester">
                        {detailData?.user?.name || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Requester Position">
                        {detailData?.user?.position?.position_name || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Request Date">
                        {detailData?.request_date ? dayjs(detailData.request_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Assign Date">
                        {detailData?.assign_date ? dayjs(detailData.assign_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Return Date">
                        {detailData?.return_date ? dayjs(detailData.return_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Status">
                        {renderStatus(detailData?.status)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Authorization Status">
                        {renderDelegationStatus(getDelegationStatus(detailData))}
                    </Descriptions.Item>

                    <Descriptions.Item label="Authorization By">
                        {getDelegationUser(detailData)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Approval Status">
                        {renderApprovalStatus(getApprovalStatus(detailData))}
                    </Descriptions.Item>

                    <Descriptions.Item label="Approved By">
                        {getApprovalUser(detailData)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Approved By Position">
                        {detailData?.approval?.approval_user?.position?.position_name || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Notes" span={2}>
                        {detailData?.notes || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Authorization Notes" span={2}>
                        {detailData?.approval?.delegation_notes || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Approval Notes" span={2}>
                        {detailData?.approval?.approval_notes || '-'}
                    </Descriptions.Item>
                </Descriptions>
                <ApprovalLine data={detailData} />
            </Modal>
        </div>
    );
}

export default AssetRequest;